"use client";

import * as React from "react";
import { Loader2 } from "lucide-react";
import { Button } from "./ui/button";

type ShadcnButtonProps = React.ComponentProps<typeof Button>;

type CustomButtonVariant =
  | "primary"
  | "outline"
  | "secondary"
  | "outline-secondary"
  | "destructive"
  | "outline-destructive"
  | "outline-primary"
  | "ghost"
  | "ghost-primary"
  | "ghost-destructive"
  | "link"
  | "link-destructive"
  | "icon";

type CustomButtonProps = Omit<ShadcnButtonProps, "variant"> & {
  loading?: boolean;
  label?: React.ReactNode;
  variant?: CustomButtonVariant;
};

const BASE_STYLES = "font-[600]";

const VARIANT_STYLES: Record<CustomButtonVariant, string> = {
  primary: "bg-primary hover:bg-primary/90 text-primary-foreground",
  outline: "border border-gray-300 bg-background text-foreground hover:bg-gray-50",
  secondary: "bg-secondary hover:bg-secondary/80 text-secondary-foreground",
  "outline-secondary": "border border-secondary text-secondary hover:bg-secondary/10",
  destructive: "bg-destructive hover:bg-destructive/90 text-destructive-foreground",
  "outline-destructive": "border border-destructive text-destructive hover:bg-destructive/10",
  "outline-primary":
    "border border-primary text-primary hover:bg-primary/10 dark:border-foreground dark:text-foreground dark:hover:bg-foreground/10",
  ghost: "bg-transparent hover:bg-accent hover:text-accent-foreground",
  "ghost-primary": "bg-transparent hover:bg-primary/10 text-primary hover:text-primary",
  "ghost-destructive":
    "bg-transparent hover:bg-destructive/10 text-destructive hover:text-destructive",
  link: "bg-transparent text-primary hover:underline",
  "link-destructive": "bg-transparent text-destructive hover:underline",
  icon: "",
};

/**
 * With `asChild` the content is handed to Radix's `Slot`, which accepts exactly
 * one element child: wrapping it — or appending `label` and the spinner beside it
 * — makes `Slot` throw, so that mode renders the caller's element untouched.
 */
function renderButtonContent({
  asChild,
  loading,
  children,
  label,
}: Readonly<{
  asChild: boolean;
  loading?: boolean;
  children?: React.ReactNode;
  label?: React.ReactNode;
}>) {
  if (asChild) {
    return children;
  }
  if (loading) {
    return <Loader2 className="animate-spin" />;
  }
  return (
    <>
      {children}
      {label}
    </>
  );
}

const ButtonComponent = React.forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      type = "button",
      loading,
      disabled,
      onClick,
      label,
      children,
      variant = "primary",
      className = "",
      size,
      asChild = false,
      ...rest
    },
    ref,
  ) => {
    const variantStyles = VARIANT_STYLES[variant];
    const buttonType = asChild ? undefined : type;

    return (
      <Button
        ref={ref}
        type={buttonType}
        size={size}
        className={`${BASE_STYLES} ${variantStyles} ${className} cursor-pointer`}
        variant={variant as ShadcnButtonProps["variant"]}
        disabled={loading || disabled}
        onClick={onClick}
        asChild={asChild}
        {...rest}
      >
        {renderButtonContent({ asChild, loading, children, label })}
      </Button>
    );
  },
);

ButtonComponent.displayName = "ButtonComponent";

export default ButtonComponent;
